import { TextField } from '@ads/components-react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@/shared/ui/atoms';

import { useParcelFormError } from '../../../hooks/useParcelFormError';
import type { ParcelFormValues } from '../../../hooks/useParcelFormSchema';
import type { ParcelFieldName } from '../../../types/parcelEdit';
import { isUnseededField } from '../../../types/parcelEdit';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/** Props de `ParcelInvoiceSection`. */
export interface ParcelInvoiceSectionProps {
  readonly form: UseFormReturn<ParcelFormValues>;
  /** Campos que a fonte de leitura não consegue semear. Vem de `useParcelEditSeed`. */
  readonly disabledFields?: readonly ParcelFieldName[] | undefined;
}

/**
 * Seção de nota fiscal da parcela.
 *
 * Existe só na edição, e os dois campos daqui têm um estatuto que nenhum outro do
 * formulário tem: segundo o contrato do `PUT`, **numa parcela paga eles são os
 * únicos que o backend aceita alterar**. Ficam numa seção própria em vez de
 * espremidos em "Valores" justamente por isso — quando a parcela já foi quitada,
 * esta é a única seção que ainda produz efeito.
 *
 * A data de emissão está entre os campos sem semeadura: a grid do contrato devolve
 * `invoiceNumber`, mas não a data.
 */
export function ParcelInvoiceSection({ form, disabledFields }: ParcelInvoiceSectionProps) {
  const { t } = useTranslation('individualPayment');
  const errorOf = useParcelFormError(form);

  const isIssueDateUnseeded = isUnseededField(disabledFields, 'invoiceIssueDate');
  const issueDateError = errorOf('invoiceIssueDate');

  /** Erro tem precedência; sem ele, a lacuna de leitura é o que precisa ser dito. */
  const issueDateHelper =
    issueDateError ?? (isIssueDateUnseeded ? t('parcelEdit.values.unseededHint') : undefined);

  return (
    <PlanSectionCard title={t('parcelEdit.invoice.title')} icon={DocumentTextIcon}>
      <div className="flex flex-wrap gap-xxxs">
        <div className="flex flex-1 basis-64 flex-col">
          <TextField
            label={t('parcelEdit.invoice.number')}
            size="md"
            fullWidth
            custom={{ root: 'w-full', input: 'w-full' }}
            placeholder={t('parcelEdit.invoice.numberPlaceholder')}
            {...form.register('invoiceNumber')}
          />
        </div>

        <div className="flex flex-1 basis-64 flex-col">
          <Controller
            control={form.control}
            name="invoiceIssueDate"
            render={({ field }) => (
              <DatePicker
                label={`${t('parcelEdit.invoice.issueDate')} *`}
                size="md"
                disabled={isIssueDateUnseeded}
                value={typeof field.value === 'string' ? field.value : ''}
                onChange={field.onChange}
                hasError={issueDateError !== undefined}
                {...(issueDateHelper === undefined ? {} : { helper: issueDateHelper })}
              />
            )}
          />
        </div>

        {/* Coluna vazia mantém os dois campos com a largura das demais seções. */}
        <div className="flex flex-1 basis-64" />
      </div>
    </PlanSectionCard>
  );
}
